import { useCallback, useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  FaChevronLeft,
  FaChevronRight,
  FaQuoteLeft,
  FaStar,
} from "react-icons/fa";
import SectionEyebrow from "./SectionEyebrow";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const viewport = { once: false, amount: 0.15 };

// Newest first.
const reviews = [
  {
    name: "tanya_powers",
    country: "United States",
    time: "1 month ago",
    badge: "Ongoing collaboration",
    text: "I had an outstanding experience working with this seller. They exceeded my expectations in every way. Their knowledge of Twilio was exceptional, and they quickly identified and resolved issues that I had been struggling with. Communication was prompt, professional, and clear throughout the entire process. What impressed me most was how efficiently they completed the work without sacrificing quality. I highly recommend this seller to anyone needing help with Twilio, messaging campaigns, or related technical projects. I would absolutely hire them again. Thank you for the excellent work!",
  },
  {
    name: "aaronheff941",
    country: "United States",
    time: "2 months ago",
    text: "This team was very responsive with all the questions I had and took extra steps of ensuring they could get the job done successfully before I even initiated the job. They were very professional and did way more than I would of expected right away. They completely built me a new website AND were able to get my A2P campaign delivered in 2 days! Will definitely use them again for any assistance I need when it comes to this type of work!",
  },
  {
    name: "Ianml78",
    country: "United States",
    time: "3 months ago",
    text: "I was very pleased with the results. I had tried and tried on my own to get A2P approval to send SMS via Twilio. They knew exactly what needed to happen and they did it. Within a couple days I was approved and on my way! I will definitely use them in the future if I need this type of services.",
  },
  {
    name: "jon_graphics",
    country: "United States",
    time: "3 months ago",
    text: "One of these best group of pros I have worked with on fiverr! Was having trouble navigating this area and they solved it very quickly and with lots of patient guidance and explanation. Will be using them again for this and exploring what other areas they could help me in!",
  },
  {
    name: "markvanhook",
    country: "United States",
    time: "4 months ago",
    text: "I've worked with MK Solutions multiple times, and they consistently know how to diagnose the issue we're experiencing and move our software in the right direction. Their ability to solve problems and guide development has made them a reliable partner every time.",
  },
  {
    name: "mpmcgowan22",
    country: "United States",
    time: "4 months ago",
    text: "Faster and easier than I expected. These guys got something done in 2 days which normally takes 5-7. They even went above and beyond with adjusting something on my site which isn't even included in their package price. Thanks so much!",
  },
  {
    name: "dinoguy33",
    country: "United States",
    time: "5 months ago",
    text: "Highly advanced work, incredible communication, and beyond impressive. They make the process smooth from start to finish. If you're ready to take any processes to the next level, this is the team to hire. Their knowledge is vast so don't be afraid to dream big.. they'll bring your vision to life. Highly recommend.",
  },
  {
    name: "dustinanders590",
    country: "United States",
    time: "5 months ago",
    text: "Amazing service! I can't thank you enough for all your help. You truly went above and beyond, and your prompt attention to every detail was exceptional. I really appreciate everything you did-thank you again, and keep up the outstanding work!",
  },
];

// Every review shows for max 3 seconds, then auto-advances.
const AUTOPLAY_MS = 3000;
const getDuration = () => AUTOPLAY_MS;

const Stars = ({ size = "sm" }) => (
  <div className="flex items-center gap-0.5" role="img" aria-label="5 out of 5 stars">
    {[0, 1, 2, 3, 4].map((i) => (
      <FaStar
        key={i}
        aria-hidden="true"
        className={`text-amber-400 ${size === "md" ? "h-4 w-4" : "h-3.5 w-3.5"}`}
      />
    ))}
  </div>
);

const Avatar = ({ name, active, large = false }) => (
  <span
    aria-hidden="true"
    className={`flex shrink-0 items-center justify-center rounded-full font-display font-semibold uppercase transition-colors duration-200 ${
      large ? "h-12 w-12 text-base" : "h-9 w-9 text-sm"
    } ${
      active
        ? "bg-[#8B4AFA] text-white"
        : "bg-[#8a4afa1c] text-[#6e3fc7] group-hover:bg-[#8B4AFA]/25"
    }`}
  >
    {name.charAt(0)}
  </span>
);

const controlClass =
  "flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition-[color,border-color,background-color,transform] duration-200 hover:border-[#8B4AFA] hover:bg-[#8B4AFA]/[0.06] hover:text-[#8B4AFA] active:scale-95 motion-reduce:transform-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8B4AFA]";

const ClientReviews = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [fade, setFade] = useState({ left: false, right: false });
  const tabListRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const total = reviews.length;
  const current = reviews[active];
  const duration = getDuration(current.text);
  const autoplay = !paused && !reduceMotion;

  // Show left/right edge fades only when the strip can scroll that way
  const updateFade = useCallback(() => {
    const list = tabListRef.current;
    if (!list) return;
    const max = list.scrollWidth - list.clientWidth;
    const left = list.scrollLeft > 4;
    const right = list.scrollLeft < max - 4;
    setFade((p) => (p.left === left && p.right === right ? p : { left, right }));
  }, []);

  // Keep the active reviewer centered inside the strip (mobile), without moving the page
  useEffect(() => {
    const list = tabListRef.current;
    const tab = document.getElementById(`review-tab-${active}`);
    if (list && tab && list.scrollWidth > list.clientWidth) {
      list.scrollTo({
        left: Math.max(0, tab.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2),
        behavior: reduceMotion ? "auto" : "smooth",
      });
    }
    updateFade();
  }, [active, reduceMotion, updateFade]);

  useEffect(() => {
    window.addEventListener("resize", updateFade);
    return () => window.removeEventListener("resize", updateFade);
  }, [updateFade]);

  useEffect(() => {
    if (!autoplay) return undefined;
    const timer = setTimeout(
      () => setActive((i) => (i + 1) % total),
      duration
    );
    return () => clearTimeout(timer);
  }, [active, autoplay, duration, total]);

  const go = (index, focus = false) => {
    const next = (index + total) % total;
    setActive(next);
    if (focus) document.getElementById(`review-tab-${next}`)?.focus();
  };

  const onTabKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      e.preventDefault();
      go(active + 1, true);
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault();
      go(active - 1, true);
    }
  };

  return (
    <section id="reviews" className="mk-reviews-surface py-24 lg:py-28">
      <div className="mx-auto max-w-[84rem] px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={stagger}
          className="mx-auto mb-14 max-w-4xl text-center"
        >
          <SectionEyebrow variants={reveal}>Client Reviews</SectionEyebrow>
          <motion.h2
            variants={reveal}
            className="mt-4 font-display text-3xl font-semibold leading-tight text-slate-900 [text-wrap:balance] sm:text-4xl lg:text-5xl"
          >
            What Clients Say About Our Expertise
          </motion.h2>
          <motion.p
            variants={reveal}
            className="mx-auto mt-4 max-w-2xl text-base leading-7 text-slate-600 [text-wrap:balance]"
          >
            Real feedback from clients we have worked with on web development,
            Twilio, and A2P 10DLC projects.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={reveal}
          onPointerEnter={(e) => e.pointerType === "mouse" && setPaused(true)}
          onPointerLeave={(e) => e.pointerType === "mouse" && setPaused(false)}
          onFocus={(e) => e.target.matches(":focus-visible") && setPaused(true)}
          onBlur={() => setPaused(false)}
          className="mx-auto grid max-w-6xl grid-cols-[minmax(0,1fr)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_20px_48px_-32px_rgba(76,29,149,0.28)] transition-[border-color,box-shadow] duration-300 hover:border-[#8B4AFA]/40 hover:shadow-[0_28px_60px_-30px_rgba(76,29,149,0.45)] lg:grid-cols-[22rem_minmax(0,1fr)]"
        >
          {/* Left: rating summary + reviewer list */}
          <div className="min-w-0 border-b border-slate-200 bg-slate-50/70 lg:border-b-0 lg:border-r">
            <div className="flex items-center gap-4 border-b border-slate-200 px-6 py-6">
              <span className="font-display text-5xl font-semibold leading-none text-slate-900">
                5.0
              </span>
              <div>
                <Stars size="md" />
                <p className="mt-1.5 text-sm text-slate-600">
                  Based on our client reviews
                </p>
              </div>
            </div>

            <div className="relative">
              <div
                ref={tabListRef}
                role="tablist"
                aria-label="Client reviews"
                aria-orientation="vertical"
                onKeyDown={onTabKeyDown}
                onScroll={updateFade}
                className="relative flex gap-2 overflow-x-auto p-3 [-ms-overflow-style:none] [scrollbar-width:none] lg:flex-col lg:gap-1 lg:overflow-visible [&::-webkit-scrollbar]:hidden"
              >
                {reviews.map((review, index) => {
                  const isActive = index === active;
                  return (
                    <button
                      key={review.name}
                      id={`review-tab-${index}`}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      aria-controls="review-panel"
                      tabIndex={isActive ? 0 : -1}
                      onClick={() => go(index)}
                      className={`group flex min-w-[12rem] shrink-0 items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-[background-color,box-shadow] duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8B4AFA] lg:min-w-0 ${
                        isActive
                          ? "bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)] ring-1 ring-[#8B4AFA]/35"
                          : "hover:bg-white/80 hover:ring-1 hover:ring-slate-200"
                      }`}
                    >
                      <Avatar name={review.name} active={isActive} />
                      <span className="min-w-0">
                        <span
                          className={`block truncate text-sm font-semibold transition-colors duration-200 ${
                            isActive
                              ? "text-slate-900"
                              : "text-slate-700 group-hover:text-slate-900"
                          }`}
                        >
                          {review.name}
                        </span>
                        <span className="block text-xs text-slate-500">
                          {review.time}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Edge fades: scroll hint for the reviewer strip (mobile/tablet only) */}
              <div
                aria-hidden="true"
                className={`pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-slate-50 to-transparent transition-opacity duration-200 lg:hidden ${
                  fade.left ? "opacity-100" : "opacity-0"
                }`}
              />
              <div
                aria-hidden="true"
                className={`pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-slate-50 to-transparent transition-opacity duration-200 lg:hidden ${
                  fade.right ? "opacity-100" : "opacity-0"
                }`}
              />
            </div>
          </div>

          {/* Right: selected review + footer controls */}
          <div
            id="review-panel"
            role="tabpanel"
            aria-labelledby={`review-tab-${active}`}
            className="flex min-w-0 flex-col overflow-hidden"
          >
            {/* Slide transition: every review sits in the same grid cell and is
                offset horizontally by its distance from the active one. Only the
                active (and the one leaving) is visible, so there is no fade,
                no blank gap and no height jump. */}
            <div className="grid flex-1 grid-cols-[minmax(0,1fr)] overflow-hidden">
              {reviews.map((review, index) => {
                const isActive = index === active;
                return (
                  <figure
                    key={review.name}
                    aria-hidden={!isActive}
                    style={{ transform: `translateX(${(index - active) * 100}%)` }}
                    className={`col-start-1 row-start-1 flex min-w-0 flex-col px-5 pb-6 pt-6 transition-[transform,visibility] duration-[650ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none sm:px-12 sm:pb-8 sm:pt-12 ${
                      isActive ? "visible" : "pointer-events-none invisible"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <span
                        aria-hidden="true"
                        className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#8a4afa1c] text-[#8B4AFA]"
                      >
                        <FaQuoteLeft className="h-4 w-4" />
                      </span>
                      <Stars size="md" />
                    </div>

                    <blockquote
                      className={`my-auto max-w-2xl py-6 leading-relaxed tracking-[-0.005em] text-slate-900 sm:py-8 ${
                        review.text.length > 400
                          ? "text-base sm:text-xl"
                          : "text-lg sm:text-2xl"
                      }`}
                    >
                      {review.text}
                    </blockquote>

                    <figcaption className="flex flex-wrap items-center gap-3">
                      <Avatar name={review.name} active large />
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-base font-semibold text-slate-900">
                            {review.name}
                          </p>
                          {review.badge && (
                            <span className="rounded-md bg-[#8a4afa1c] px-2 py-0.5 text-xs font-semibold text-[#6e3fc7]">
                              {review.badge}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">
                          {review.country} · {review.time}
                        </p>
                      </div>
                    </figcaption>
                  </figure>
                );
              })}
            </div>

            {/* Footer bar: segmented progress + arrows */}
            <div className="flex items-center justify-between gap-4 border-t border-slate-200 bg-slate-50/60 px-6 py-4 sm:px-12">
              <div
                role="group"
                aria-label="Review progress"
                className="flex items-center gap-1.5"
              >
                {reviews.map((review, index) => (
                  <button
                    key={review.name}
                    type="button"
                    aria-label={`Show review ${index + 1} of ${total}`}
                    aria-current={index === active}
                    onClick={() => go(index)}
                    className="group flex h-6 w-5 items-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8B4AFA] sm:w-8"
                  >
                    <span className="relative block h-1 w-full overflow-hidden rounded-full bg-slate-200 transition-[height,background-color] duration-200 group-hover:h-1.5 group-hover:bg-slate-300">
                      {index < active || (index === active && !autoplay) ? (
                        <span className="absolute inset-0 rounded-full bg-[#8B4AFA]" />
                      ) : index === active ? (
                        <motion.span
                          key={active}
                          className="absolute inset-0 origin-left rounded-full bg-[#8B4AFA]"
                          initial={{ scaleX: 0 }}
                          animate={{ scaleX: 1 }}
                          transition={{ duration: duration / 1000, ease: "linear" }}
                        />
                      ) : null}
                    </span>
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label="Previous review"
                  onClick={() => go(active - 1)}
                  className={controlClass}
                >
                  <FaChevronLeft aria-hidden="true" className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  aria-label="Next review"
                  onClick={() => go(active + 1)}
                  className={controlClass}
                >
                  <FaChevronRight aria-hidden="true" className="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ClientReviews;